import actions from '../actions';
import { VERSION } from '../config';
import { toLog } from '../screens/Services/Backup/Backup';
import {
    addToDeletedAttaches,
    getProfileInfo
} from '../screens/Services/Backup/common/backupData';
import {
    decrypt,
    encrypt
} from '../utils';
import { extractFileNameFromFullPath } from '../utils/files';
import firebaseService from './firebase';
import { storage } from './localstorage';

export const MY_DOCUMENTS_STORAGE_KEY = 'myDocuments';
export const MY_DOCUMENTS_SYNC_KEY = 'syncedVersionMyDocuments';

export const MY_DOCUMENTS_REDUX_KEY = 'myDocuments';

// Вспомогательная функция для разрешения profile/hash из аргументов или текущего состояния
const resolveProfileAndHash = ({
    profileId,
    hashKey,
} = {}) => {
    const {
        profileId: profile,
        hashKey: hash,
    } = getProfileInfo();
    return {
        profile: profileId ?? profile,
        hash: hashKey ?? hash,
    };
};

export const getMyDocumentsFromStorage = async ({
    profileId,
    hashKey,
} = {}) => {
    const {
        profile,
        hash,
    } = resolveProfileAndHash({
        profileId,
        hashKey,
    });

    if ((!profile) || (!hash)) {
        toLog('No profile or hash to get my documents from storage', {
            hash,
            hashKey,
            profile,
            profileId,
        });
        throw new Error(Object.R('errors.noProfile'));
    }
    let result = [];
    try {
        const data = await storage.getObject(MY_DOCUMENTS_STORAGE_KEY, {});
        const profileData = data?.[profile];
        if (!profileData) {
            return result;
        }
        result = decrypt(profileData, hash) ?? [];
        return result;
    } catch (error) {
        toLog('Error getting my documents from storage:', error);
        return [];
    }
};

export const addDocumentToMyDocuments = async ({ document } = {}) => {
    if (!document) {
        throw new Error('No document to add');
    }
    const {
        profileId,
        hashKey,
    } = getProfileInfo();
    const documents = await getMyDocumentsFromStorage({
        profileId,
        hashKey,
    });
    const updatedDocuments = [ ...documents, document ];
    await setMyDocumentsToStorage({
        documents: updatedDocuments,
        profileId,
        hashKey,
    });
    actions.setFormValue(MY_DOCUMENTS_REDUX_KEY, updatedDocuments);
    return updatedDocuments;
};

export const setMyDocumentsToStorage = async ({
    documents = [],
    profileId,
    hashKey,
} = {}) => {
    const {
        profile,
        hash,
    } = resolveProfileAndHash({
        profileId,
        hashKey,
    });
    if (!profile || !hash) {
        throw new Error(Object.R('errors.noProfile'));
    }
    try {
        const data = await storage.getObject(MY_DOCUMENTS_STORAGE_KEY, {});
        const encrypted = encrypt(documents, hash);
        data[profile] = encrypted;
        await storage.set(MY_DOCUMENTS_STORAGE_KEY, data);
        return encrypted;
    } catch (error) {
        toLog('Error setting my documents to storage:', error);
        throw error;
    }
};

const getDocsFromFB = async ({
    profileId,
    hashKey,
    storeKey,
}) => {
    if (!profileId || !hashKey || !storeKey) {
        return [];
    }
    const profileData = await firebaseService.getById('profile', storeKey);
    const prefs = decrypt(profileData?.prefs ?? '', hashKey) ?? {};
    return prefs?.documents ?? [];
};

export const isSyncedMyDocuments = async ({ profileId } = {}) => {
    if (!profileId) {
        return false;
    }
    const result = await storage.get(MY_DOCUMENTS_SYNC_KEY);
    const ver = VERSION?.split('.')
        .slice(0, 3)
        .join('.');
    await storage.set(MY_DOCUMENTS_SYNC_KEY, ver);
    return result;
};

export const removeMyDocumentFromStorageById = async ({
    id,
    profileId,
    hashKey,
} = {}) => {
    const {
        hashKey: hash,
        profile: profile,
    } = getProfileInfo();
    if (!id) {
        throw new Error('No document id to remove');
    }
    try {
        const documents = await getMyDocumentsFromStorage({
            profileId: profileId ?? profile,
            hashKey: hashKey ?? hash,
        });

        const doc = extractFileNameFromFullPath(documents?.find((d) => d?.id === id)?.path);

        if (doc) {
            await addToDeletedAttaches(doc);
        }

        const updatedDocuments = documents?.filter?.(doc => doc.id !== id);
        await setMyDocumentsToStorage({
            documents: updatedDocuments,
            profileId,
            hashKey,
        });
        actions.setFormValue(MY_DOCUMENTS_REDUX_KEY, updatedDocuments);
        return updatedDocuments;
    } catch (e) {
        console.warn('removeMyDocumentFromStorageById error', e);
        return [];
    }
};


export const onLoadMyDocuments = async (params = {}) => {
    const {
        me: { profileId } = {},
        hashKey,
        storeKey,
    } = params;
    const isSynced = await isSyncedMyDocuments({ profileId });
    let docs = [];
    if (isSynced) {
        docs = await getMyDocumentsFromStorage({
            profileId,
            hashKey,
        });
    } else {
        docs = await getDocsFromFB({
            profileId,
            hashKey,
            storeKey,
        });
        await setMyDocumentsToStorage({
            documents: docs,
            profileId,
            hashKey,
        });
    }
    actions.setFormValue(MY_DOCUMENTS_REDUX_KEY, docs);
};
